import type { RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'

/**
 * The employees module's routes (WP-02, wp/employees §3). Paths and names from the route manifest (D-15); the legacy
 * paths (`/:software/employees/view/:id`, `/:software/create-employee`, `/:software/view-employee-group/:id`, …)
 * redirect here through `src/app/router/legacy-redirects.ts`. Create / edit pages need Employees write (D-21); the
 * rest Employees read.
 *
 * `:software` is navigation context only (§1.1): the record asks the API for the employee view whatever it is.
 */
const listCrumb = { text: keys.t('employees.list.title.employees'), to: { name: 'employees' } }
const teamsCrumb = { text: keys.t('employees.teamList.title.teams'), to: { name: 'employee-groups' } }
/** Create, edit: Employees › Employee list › Employee (legacy `beforeEnter`). */
const employeeCrumbs = [
  listCrumb,
  { text: keys.t('employees.list.title.employeeList'), to: { name: 'employees' } },
  { text: keys.t('employees.edit.title.employee'), active: true },
]
/** Team view, create, edit: Employees › Teams › Team (legacy `beforeEnter`; the static create crumbs said Teams twice). */
const teamCrumbs = [listCrumb, teamsCrumb, { text: keys.t('employees.teamView.title.team'), active: true }]

const routes: RouteRecordRaw[] = [
  {
    path: '/:software/employees',
    name: 'employees',
    component: () => import('./views/EmployeeListView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      pageTitle: keys.t('employees.list.title.employeeList'),
      breadcrumb: [listCrumb, { text: keys.t('employees.list.title.employeeList'), active: true }],
    },
  },
  {
    path: '/:software/employees/:id(\\d+)',
    name: 'view-employee',
    component: () => import('./views/EmployeeDetailView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      navActiveLink: 'employees',
      pageTitle: keys.t('employees.detail.title.employee'),
      breadcrumb: [
        listCrumb,
        { text: keys.t('employees.list.title.employeeList'), to: { name: 'employees' } },
        { text: keys.t('employees.detail.title.employee'), active: true },
      ],
    },
  },
  {
    // The shared import page (2026-11, docs/import/PLAN.md P2), from the list and the org chart.
    path: '/:software/employees/import',
    name: 'employee-import',
    component: () => import('./views/EmployeeImportView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      action: 'write',
      navActiveLink: 'employees',
      pageTitle: keys.t('employees.import.title'),
      breadcrumb: [listCrumb, { text: keys.t('employees.import.title'), active: true }],
    },
  },
  {
    path: '/:software/employees/new',
    name: 'create-employee',
    component: () => import('./views/EmployeeEditView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      action: 'write',
      navActiveLink: 'employees',
      pageTitle: keys.t('employees.edit.title.employee'),
      breadcrumb: employeeCrumbs,
    },
  },
  {
    path: '/:software/employees/:id(\\d+)/edit',
    name: 'edit-employee',
    component: () => import('./views/EmployeeEditView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      action: 'write',
      navActiveLink: 'employees',
      pageTitle: keys.t('employees.edit.title.employee'),
      breadcrumb: employeeCrumbs,
    },
  },
  {
    // `?fit=yes` opens the chart zoomed to fit (legacy read `window.location`, here the route query).
    path: '/:software/org-chart',
    name: 'org-chart',
    component: () => import('./views/OrgChartView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      pageTitle: keys.t('employees.orgChart.title.organisationChart'),
      breadcrumb: [{ text: keys.t('employees.orgChart.title.organisationChart'), active: true }],
    },
  },
  {
    path: '/:software/employee-groups',
    name: 'employee-groups',
    component: () => import('./views/TeamListView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      pageTitle: keys.t('employees.teamList.title.teams'),
      breadcrumb: [listCrumb, { text: keys.t('employees.teamList.title.teams'), active: true }],
    },
  },
  {
    path: '/:software/employee-groups/new',
    name: 'create-employee-group',
    component: () => import('./views/TeamEditView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      action: 'write',
      navActiveLink: 'employee-groups',
      pageTitle: keys.t('employees.teamEdit.title.createTeam'),
      breadcrumb: teamCrumbs,
    },
  },
  {
    path: '/:software/employee-groups/:id(\\d+)',
    name: 'view-employee-group',
    component: () => import('./views/TeamDetailView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      navActiveLink: 'employee-groups',
      pageTitle: keys.t('employees.teamView.title.viewTeam'),
      breadcrumb: teamCrumbs,
    },
  },
  {
    path: '/:software/employee-groups/:id(\\d+)/edit',
    name: 'edit-employee-group',
    component: () => import('./views/TeamEditView.vue'),
    meta: {
      software: 'common',
      resource: 'employees',
      action: 'write',
      navActiveLink: 'employee-groups',
      pageTitle: keys.t('employees.teamEdit.title.editTeam'),
      breadcrumb: teamCrumbs,
    },
  },
]

export default routes
